import React , { useState, useRef } from "react";
import "./style.css";

// React 18.4 compatible libraries
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  TableSortLabel,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";

// Import icons
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Custom scrollbar component
import { Scrollbars } from "react-custom-scrollbars-2";

// Import CSS module styles
import classes from "./SalesTable.module.css";

// Apply your existing styles with styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  overflow: "visible",
}));

const StyledTable = styled(Table)(({ theme }) => ({
  tableLayout: "fixed", // Force fixed layout for better column control
  "& tbody tr:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
    zIndex: "-1000",
  },
  "& tbody tr:hover": {
    backgroundColor: "white",
    boxShadow: "0 8px 16px 0 rgba(0,0,0,0.2)",
    color: "#1777C4",
  },
}));

// Fixed header styling with improved alignment
const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: "transparent",
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  color: "#355676ef",
  boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
  backgroundColor: "white",
  fontWeight: "bolder",
  padding: "0.5rem 1rem", 
  lineHeight: "1.2rem",
  position: "sticky",
  top: "0",
  fontSize: "14px",
  zIndex: "100",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    boxShadow: "-8px 8px 16px 0 rgba(0,0,0,0.2)",
  },
}));

const StyledFilterCell = styled(TableCell)(({ theme }) => ({
  padding: "0.25rem 0.5rem",
  backgroundColor: "white",
  position: "sticky",
  top: "40px", // Position below header row
  zIndex: "99",
  boxShadow: "0 2px 4px 0 rgba(0,0,0,0.1)",
}));

// Compact filter field styling
const StyledFilterField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    height: "1.8rem",
    fontSize: "12px",
  },
  "& .MuiInputBase-input": {
    padding: "2px 0",
  },
}));

const StyledPaginationContainer = styled(Box)(({ theme }) => ({
  backgroundImage: "linear-gradient(to top, #dfe9f3 0%, white 100%)",
  backgroundColor: "white",
  boxShadow: "-8px 8px 16px 8px rgba(0,0,0,0.2)",
  position: "sticky",
  right: "0",
  bottom: "0",
  height: "3.7rem",
  color: "#355676ef",
  fontWeight: "900 !important",
  "&:hover": {
    boxShadow: "-8px 8px 16px 8px rgba(0,0,0,0.2)",
  },
  zIndex: "0",
}));

const StyledDataCell = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Montserrat',
  fontSize: '0.7rem',
  padding: '12px !important',
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    color: "#1777C4",
  },
  "&.truncated": {
    maxWidth: 0,
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100% !important",
  display: "block !important",
  marginTop: "1rem",
  marginBottom: "2rem",
  backgroundImage: "linear-gradient(to top, #dfe9f3 0%, white 100%)",
  fontWeight: "900 !important",
  textTransform: "capitalize !important",
}));

const TableViewContainer = styled(Box)(({ theme }) => ({
  width: "100% !important",
  display: "block !important",
  marginTop: "0.5rem",
  marginBottom: "2rem",
  backgroundColor: "transparent",
}));

// Icon styles
const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  padding: "4px",
  color: "#355676ef",
  "&:hover": {
    color: "#1777C4",
  },
}));

// Icon containers
const DeleteIconStyled = styled(DeleteIcon)(({ theme }) => ({
  fontSize: "1rem",
  color: "#e53935",
  "&:hover": {
    color: "#c62828",
  },
}));

const ViewIconStyled = styled(VisibilityIcon)(({ theme }) => ({
  fontSize: "1rem",
  color: "#1777C4",
  "&:hover": {
    color: "#0d47a1",
  },
}));

const CellContent = ({ value, maxChars = 30 }) => {
  // Check if value is a React element (for custom components)
  if (React.isValidElement(value)) {
    return value;
  }
  
  const stringValue = String(value || '');
  const needsTooltip = stringValue.length > maxChars;
  
  return needsTooltip ? (
    <Tooltip title={stringValue} placement="top">
      <span className={classes.Cell}>
        {stringValue.substring(0, maxChars)}...
      </span>
    </Tooltip>
  ) : (
    <span className={classes.Cell}>{stringValue}</span>
  );
};

const CurrencyFormatter = ({ value }) => (
  <CellContent value={value} />
);

const LeaveFormatter = ({ value, leaveTypes }) => {
  const findIndex = leaveTypes?.rows?.findIndex(
    (element) => element.LEAVETYPE_CODE === value
  );
  
  const displayValue = findIndex === -1 || !leaveTypes?.rows
    ? value
    : leaveTypes.rows[findIndex]["LEAVETYPE_DESC"];
    
  return <CellContent value={displayValue} />;
};

const PeriodFormatter = ({ value, periodList }) => {
  const findIndex = periodList?.rows?.findIndex((element) => element.KEY === value);
  
  const displayValue = findIndex === -1 || !periodList?.rows
    ? value
    : periodList.rows[findIndex]["VALUE"];
    
  return <CellContent value={displayValue} />;
};

// Custom resize handle component
const ResizeHandle = styled("div")(({ active }) => ({
  position: "absolute",
  right: 0,
  top: 0,
  height: "100%",
  width: "8px",
  cursor: "col-resize",
  zIndex: 1000,
  userSelect: "none",
  backgroundColor: active ? "#1777C4" : "transparent",
  "&:hover": {
    backgroundColor: "#ddd",
  },
}));

const TableRenderer = (props) => {
  const [columns] = useState(props.columns);
  const [rows, setRows] = useState(props.data);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(rows.length > 10 ? 10 : rows.length);
  const pageSizes = rows.length > 100
    ? [10, 50, 100, rows.length]
    : rows.length > 50
    ? [10, 50, rows.length]
    : rows.length > 10
    ? [10, rows.length]
    : [rows.length];

  // Initialize column widths based on content
  const [columnWidths, setColumnWidths] = useState(() => {
    return props.columns.map((col) => {
      if (col.name === "D" || col.name === "V")
        return { columnName: col.name, width: 70 };
      else if (col.name === "SNO") 
        return { columnName: col.name, width: 80 };
      else if (col.name === "ATTEND_CODE" || col.name === "EMP_CODE")
        return { columnName: col.name, width: 0 };
      else if (col.name === "Item Name" || col.name.includes("Name"))
        return { columnName: col.name, width: 220 };
      else if (col.name === "Category Desc") 
        return { columnName: col.name, width: 160 };
      else if (col.name === "manage_rights" || col.name === "actions")
        return { columnName: col.name, width: 120 }; // Add specific width for action buttons
      else
        return { columnName: col.name, width: 140 };
    });
  });

  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [filters, setFilters] = useState({});
  
  // Refs for column resizing
  const tableRef = useRef(null);
  const [resizing, setResizing] = useState(null);

  // Sort handling
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter handling
  const handleFilterChange = (columnName, value) => {
    setFilters(prev => ({
      ...prev,
      [columnName]: value
    }));
  };

  // Page handling
  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  // Column resize handlers
  const handleResizeStart = (e, columnName) => {
    e.stopPropagation(); // Prevent sort triggering
    
    const initialX = e.clientX;
    const columnWidth = columnWidths.find(c => c.columnName === columnName)?.width || 100;
    
    const handleMouseMove = (moveEvent) => {
      const currentX = moveEvent.clientX;
      const difference = currentX - initialX;
      const newWidth = Math.max(60, columnWidth + difference); // Min width of 60px
      
      setColumnWidths(prev => 
        prev.map(col => 
          col.columnName === columnName 
            ? { ...col, width: newWidth } 
            : col
        )
      );
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setResizing(null);
    };
    
    // Set active resizing state
    setResizing(columnName);
    
    // Add and remove listeners for mousemove and mouseup
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle view action
  const handleView = (row) => {
    console.log("View row:", row);
    // Implement your view logic here
    if (props.onView) {
      props.onView(row);
    }
  };

  // Handle delete action
  const handleDelete = (row) => {
    console.log("Delete row:", row);
    // Implement your delete logic here
    if (props.onDelete) {
      props.onDelete(row);
    }
  };

  // Apply filters to data
  const getFilteredData = () => {
    return rows.filter(row => {
      return Object.keys(filters).every(columnName => {
        const filterValue = filters[columnName]?.toLowerCase();
        if (!filterValue) return true;
        
        const cellValue = String(row[columnName] || '')?.toLowerCase();
        return cellValue?.includes(filterValue);
      });
    });
  };

  // Apply sorting to data
  const getSortedData = (data) => {
    if (!orderBy) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[orderBy] || '';
      const bValue = b[orderBy] || '';
      
      if (order === 'asc') {
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      } else {
        if (aValue > bValue) return -1;
        if (aValue < bValue) return 1;
        return 0;
      }
    });
  };

  // Apply pagination to data
  const getPaginatedData = (data) => {
    if (pageSize === rows.length) return data;
    const startIndex = currentPage * pageSize;
    return data.slice(startIndex, startIndex + pageSize);
  };

  // Format cell based on column type
  const formatCell = (row, columnName) => {
    // Check if we have a custom formatter from parent component
    if (props.formatter) {
      const customContent = props.formatter(row, columnName);
      if (customContent !== undefined) {
        return customContent;
      }
    }
    
    // For icon columns
    if (columnName === "D") {
      return (
        <IconButtonStyled onClick={() => handleDelete(row)} size="small">
          <DeleteIconStyled />
        </IconButtonStyled>
      );
    }
    
    if (columnName === "V") {
      return (
        <IconButtonStyled onClick={() => handleView(row)} size="small">
          <ViewIconStyled />
        </IconButtonStyled>
      );
    }
    
    const value = row[columnName];
    
    // Handle leave type columns
    if (["LEAVE_TYPE", "LEAVE_TYPE2"].includes(columnName)) {
      return <LeaveFormatter value={value} leaveTypes={props.leaveTypes} />;
    }
    
    // Handle period columns
    if (["LEAVE_PERIOD", "LEAVE_PERIOD2"].includes(columnName)) {
      return <PeriodFormatter value={value} periodList={props.periodList} />;
    }
    
    // Default formatting
    return <CurrencyFormatter value={value} />;
  };

  // Process data through filtering, sorting, and pagination
  const filteredData = getFilteredData();
  const sortedData = getSortedData(filteredData);
  const displayData = getPaginatedData(sortedData);

  return (
    <TableViewContainer className={classes.TableView}>
      <StyledPaper square elevation={0} className={classes.Paper}>
        <Scrollbars
          autoHeight
          autoHeightMax={
            props.val
              ? window.innerHeight * 0.75
              : window.innerHeight * 0.65
          }
        >
          <StyledTableContainer ref={tableRef}>
            <StyledTable stickyHeader>
              <StyledTableHead>
                {/* Header row with column titles and sort functionality */}
                <TableRow>
                  {columns.map((column) => {
                    const colWidth = columnWidths.find(c => c.columnName === column.name)?.width || 'auto';
                    const display = column.name === "ATTEND_CODE" || column.name === "EMP_CODE" ? 'none' : 'table-cell';
                    
                    return (
                      <StyledHeaderCell
                        key={column.name}
                        style={{ 
                          width: colWidth, 
                          display, 
                          minWidth: colWidth,
                          position: 'relative' // For resize handle positioning
                        }}
                        onClick={() => column.name !== "D" && column.name !== "V" && handleRequestSort(column.name)}
                      >
                        {column.name === "D" || column.name === "V" ? (
                          column.title
                        ) : (
                          <TableSortLabel
                            active={orderBy === column.name}
                            direction={orderBy === column.name ? order : 'asc'}
                            hideSortIcon={column.name === "D" || column.name === "V"}
                          >
                            {column.title}
                          </TableSortLabel>
                        )}
                        
                        <ResizeHandle 
                          active={resizing === column.name}
                          onMouseDown={(e) => handleResizeStart(e, column.name)}
                        />
                      </StyledHeaderCell>
                    );
                  })}
                </TableRow>
                
                {/* Filter row with input fields */}
                <TableRow>
                  {columns.map((column) => {
                    const display = column.name === "ATTEND_CODE" || column.name === "EMP_CODE" ? 'none' : 'table-cell';
                    const colWidth = columnWidths.find(c => c.columnName === column.name)?.width || 'auto';
                    
                    return (
                      <StyledFilterCell
                        key={`filter-${column.name}`}
                        style={{ display, width: colWidth, minWidth: colWidth }}
                      >
                        {column.name !== "D" && column.name !== "V" && 
                         column.name !== "manage_rights" && column.name !== "actions" ? (
                          <StyledFilterField
                            variant="standard"
                            placeholder="Filter..."
                            size="small"
                            fullWidth
                            value={filters[column.name] || ''}
                            onChange={(e) => handleFilterChange(column.name, e.target.value)}
                            InputProps={{
                              style: { fontSize: '12px' }
                            }}
                          />
                        ) : null}
                      </StyledFilterCell>
                    );
                  })}
                </TableRow>
              </StyledTableHead>
              
              <TableBody>
                {displayData.length > 0 ? (
                  displayData.map((row, index) => (
                    <TableRow 
                      key={row.id || index}
                      className={index % 2 === 1 ? classes.oddRow : ''}
                    >
                      {columns.map((column) => {
                        const colWidth = columnWidths.find(c => c.columnName === column.name)?.width || 'auto';
                        return (
                          <StyledDataCell
                            key={`${row.id || index}-${column.name}`}
                            style={{ 
                              display: column.name === "ATTEND_CODE" || column.name === "EMP_CODE" ? 'none' : 'table-cell',
                              width: colWidth,
                              minWidth: colWidth,
                              maxWidth: colWidth,
                              textAlign: (column.name === "D" || column.name === "V" || 
                                          column.name === "manage_rights" || column.name === "actions") ? 
                                          "center" : "left"
                            }}
                            className={column.name === "manage_rights" || column.name === "actions" ? "" : "truncated"}
                          >
                            {formatCell(row, column.name)}
                          </StyledDataCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <StyledDataCell colSpan={columns.length} align="center">
                      No data found
                    </StyledDataCell>
                  </TableRow>
                )}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        </Scrollbars>
        
        <StyledPaginationContainer>
          <TablePagination
            rowsPerPageOptions={pageSizes}
            component="div"
            count={filteredData.length}
            rowsPerPage={pageSize}
            page={currentPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </StyledPaginationContainer>
      </StyledPaper>
    </TableViewContainer>
  );
};

export default TableRenderer;